import { useOpenModal } from '@flowus/common/next-modal';
import { PayType } from '@flowus/common/utils/pay-info';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Button } from 'src/common/components/button';
import { Input } from 'src/common/components/input';
import { message } from 'src/common/components/message';
import { request } from 'src/common/request';
import { Modals } from 'src/modals';
import { PayQrModal } from './select-upgrade-plan/pay-qr-modal';
import { refreshSpaceData } from 'src/services/app/hook/refresh-space';
import { getCurrencySymbols } from '@flowus/common';

/** 单价：10 元/人 */
const UNIT_PRICE = 10;
/** 最小购买数量 */
const MIN_COUNT = 1;
/** 最大购买数量 */
const MAX_COUNT = 1000;

export interface BuyGuestCountModalProps {
  spaceId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** 购买外部协作者弹窗 */
export const BuyGuestCountModal: FC<BuyGuestCountModalProps> = (props) => {
  const { spaceId, onClose, onSuccess } = props;
  const openModal = useOpenModal();
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);

  // 总金额
  const totalAmount = useMemo(() => {
    return UNIT_PRICE * count;
  }, [count]);

  // 数量变化处理
  const handleCountChange = (value: number) => {
    if (value < MIN_COUNT) {
      setCount(MIN_COUNT);
    } else if (value > MAX_COUNT) {
      setCount(MAX_COUNT);
    } else {
      setCount(Math.floor(value));
    }
  };

  // 创建订单
  const createOrder = async (payType: PayType = PayType.wxpay) => {
    const ret = await request.infra.createGuestCountOrder({
      spaceId,
      count,
      payType,
    });
    return ret;
  };

  // 支付成功后回调
  const handlePaySuccess = async () => {
    await refreshSpaceData(spaceId);
    message.success(`购买成功！您已新增 ${count} 名外部协作者名额。`);
    onSuccess?.();
  };

  // 去支付
  const handlePay = async () => {
    if (loading) return;

    if (count < MIN_COUNT || count > MAX_COUNT) {
      message.warning(`购买数量必须在 ${MIN_COUNT} - ${MAX_COUNT} 之间`);
      return;
    }

    setLoading(true);
    try {
      const order = await createOrder();

      if (!order.orderId || !order.qrCodeUrl) {
        message.error('创建订单失败');
        return;
      }

      onClose();

      openModal.modal({
        modalId: Modals.PAY_MODAL,
        content: () => (
          <PayQrModal
            orderId={order.orderId}
            qrCodeUrl={order.qrCodeUrl ?? ''}
            payType={PayType.wxpay}
            priceTitle={`${getCurrencySymbols()}${totalAmount}`}
            rePayOrder={() => createOrder()}
            onFinish={async () => {
              await handlePaySuccess();
            }}
          />
        ),
      });
    } catch (error) {
      message.error((error as Error).message || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-[400px] p-6">
      <div className="mb-6 text-h3">购买更多外部协作者</div>

      {/* 数量输入 */}
      <div className="mb-4 flex items-center justify-between text-t2">
        <span className="text-grey3">购买数量</span>
        <div className="flex items-center space-x-2">
          <Button
            className="h-8 w-8 !p-0"
            colorType="secondary"
            onClick={() => handleCountChange(count - 1)}
            disable={count <= MIN_COUNT}
          >
            -
          </Button>
          <Input
            type="number"
            inputType="number"
            value={count.toString()}
            onChange={(value) => handleCountChange(Number(value))}
            className="h-8 w-20 text-center"
            inputClassName="text-center"
          />
          <Button
            className="h-8 w-8 !p-0"
            colorType="secondary"
            onClick={() => handleCountChange(count + 1)}
            disable={count >= MAX_COUNT}
          >
            +
          </Button>
        </div>
      </div>

      {/* 单价 */}
      <div className="mb-4 flex items-center justify-between text-t2">
        <span className="text-grey3">单价</span>
        <span>{UNIT_PRICE} 元/人</span>
      </div>

      {/* 总金额 */}
      <div className="mb-4 flex items-center justify-between rounded bg-grey9 px-4 py-3">
        <span className="text-t2-medium">总金额</span>
        <span className="text-h3 text-active_color">{`${getCurrencySymbols()}${totalAmount}`}</span>
      </div>

      {/* 警告提示 */}
      <div className="mb-6 rounded bg-red/10 px-3 py-2 text-t4 text-red">
        ⚠️
        提示：新增外部协作者名额与您的空间会员身份相关联。若会员失效，额外名额将自动失效。请保持空间会员状态，以确保新增协作者正常使用。
      </div>

      {/* 操作按钮 */}
      <div className="flex justify-end space-x-3">
        <Button colorType="secondary" onClick={onClose}>
          取消
        </Button>
        <Button colorType="active" onClick={handlePay} disable={loading}>
          {loading ? '处理中...' : '确认购买'}
        </Button>
      </div>
    </div>
  );
};

/** 打开购买外部协作者弹窗的 hook */
export const useBuyGuestCount = () => {
  const openModal = useOpenModal();

  return (props: Omit<BuyGuestCountModalProps, 'onClose'>) => {
    openModal.modal({
      content: ({ onCloseModal }) => <BuyGuestCountModal {...props} onClose={onCloseModal} />,
    });
  };
};
